import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useEffect, useMemo, useState } from "react";

import {
  computeOptionSummaries,
  copyTargetStates,
  dismissSkipReasonsById,
  unmatchedCopyWarnings,
  willUpdateCell,
} from "./bulkResolvePreview.helpers";
import {
  type BulkAnswer,
  type BulkPlan,
  type BulkResolveRow,
  computeBulkOutcomes,
  computeBulkPlan,
  countBulkOutcomes,
  formatBulkErrorMessage,
  formatBulkTitle,
  formatBulkToastMessage,
  groupByApplyTo,
  isBulkSource,
  sourceDisabledReason,
} from "./bulkResolveWithValue.helpers";
import { DISCREPANCY_MUTATION_REFETCH_QUERIES } from "./discrepancyRefetchQueries";
import { type ActionKey, countSavedStatuses } from "./DiscrepancyReviewDialog.helpers";
import { type ResolveActionOption } from "./ResolveDiscrepanciesDialog";
import { CORRECT_VALUE_SOURCE, RESOLUTION_TARGET } from "./resolveMutationEnums";
import { RESOLVE_DISCREPANCIES } from "./useResolveWithValueForm";

export interface UseBulkResolveWithValueFormArgs {
  open: boolean;
  rows: BulkResolveRow[];
  clearSelection: () => void;
  dismissOption: ResolveActionOption;
  cantDetermineOption?: ResolveActionOption;
  otherMutationLoading: boolean;
  onClose: () => void;
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  notifyMutated: () => void;
}

function computeSaveDisabled(opts: {
  loading: boolean;
  answer: BulkAnswer | null;
  plan: BulkPlan | null;
  dismissOption: ResolveActionOption;
  cantDetermineOption?: ResolveActionOption;
}): boolean {
  const { loading, answer, plan, dismissOption, cantDetermineOption } = opts;
  if (loading || !answer) return true;
  if (answer === "DISMISS") return dismissOption.eligibleIds.length === 0;
  if (answer === "CANT_DETERMINE") return !cantDetermineOption;
  return !plan || plan.updatable.length === 0;
}

/** Sends one resolveDiscrepancies call per applyTo group, sequentially, stopping (and reporting how many already succeeded) on the first failure. */
function useBulkSaveHandler(args: {
  rows: readonly BulkResolveRow[];
  answer: BulkAnswer | null;
  plan: BulkPlan | null;
  note: string;
  dismissOption: ResolveActionOption;
  cantDetermineOption?: ResolveActionOption;
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  clearSelection: () => void;
  onClose: () => void;
  notifyMutated: () => void;
}) {
  const alerts = useAlerts();
  const [resolve, { loading: resolving }] = useMutation(RESOLVE_DISCREPANCIES, {
    refetchQueries: DISCREPANCY_MUTATION_REFETCH_QUERIES,
  });

  const handleSave = async () => {
    const { rows, answer, plan, note, dismissOption, cantDetermineOption, onConfirm } = args;
    if (!answer) return;
    if (answer === "DISMISS") {
      onConfirm("nonDiscrepancy", dismissOption.eligibleIds, note || null);
      return;
    }
    if (answer === "CANT_DETERMINE" && cantDetermineOption) {
      onConfirm("cantDetermine", cantDetermineOption.eligibleIds, note || null);
      return;
    }
    if (!plan || plan.updatable.length === 0 || !isBulkSource(answer)) return;

    const groups = groupByApplyTo(plan.updatable);
    const savedRows: { status?: string | null }[] = [];
    let succeeded = 0;
    try {
      for (const group of groups) {
        const { data } = await resolve({
          variables: {
            input: {
              discrepancyIds: group.ids,
              correctValueSource: CORRECT_VALUE_SOURCE[answer],
              applyTo: group.applyTo.map((t) => RESOLUTION_TARGET[t]),
              note: note || undefined,
            },
          },
        });
        savedRows.push(...(data?.resolveDiscrepancies ?? []));
        succeeded += group.ids.length;
      }
      alerts.success(
        formatBulkToastMessage(countSavedStatuses(savedRows), rows.length - succeeded)
      );
      args.notifyMutated();
      args.clearSelection();
      args.onClose();
    } catch (err) {
      // Earlier groups already committed, so the caller's queue counts still need a refresh.
      if (succeeded > 0) args.notifyMutated();
      alerts.error(formatBulkErrorMessage(succeeded), err instanceof Error ? err : undefined);
    }
  };

  return { handleSave, resolving };
}

/** The plan, option captions, copy warnings, and "Will update" cells for the current answer. */
function useBulkPreview(args: {
  rows: readonly BulkResolveRow[];
  answer: BulkAnswer | null;
  dismissOption: ResolveActionOption;
  cantDetermineOption?: ResolveActionOption;
}) {
  const { rows, answer, dismissOption, cantDetermineOption } = args;
  const plan = useMemo(
    () => (answer && isBulkSource(answer) ? computeBulkPlan(rows, answer) : null),
    [rows, answer]
  );
  const optionSummaries = useMemo(
    () => computeOptionSummaries(rows, dismissOption, cantDetermineOption),
    [rows, dismissOption, cantDetermineOption]
  );
  const outcomes = useMemo(
    () => (answer && isBulkSource(answer) && plan ? computeBulkOutcomes(rows, answer, plan) : null),
    [rows, answer, plan]
  );
  const outcomeCounts = useMemo(() => countBulkOutcomes(outcomes ?? new Map()), [outcomes]);
  const copyWarnings = useMemo(
    () =>
      answer && isBulkSource(answer) && outcomes
        ? unmatchedCopyWarnings(rows, answer, outcomes)
        : [],
    [rows, answer, outcomes]
  );
  const dismissSkipReasons = useMemo(
    () => dismissSkipReasonsById(rows, dismissOption),
    [rows, dismissOption]
  );
  const willUpdateById = useMemo(
    () =>
      new Map(
        rows.map((r) => [
          r.id,
          willUpdateCell({
            id: r.id,
            status: r.status,
            answer,
            plan,
            outcomes: outcomes ?? undefined,
            dismissOption,
            dismissSkipReasons,
            cantDetermineOption,
          }),
        ])
      ),
    [rows, answer, plan, outcomes, dismissOption, dismissSkipReasons, cantDetermineOption]
  );

  return { plan, optionSummaries, copyWarnings, willUpdateById, outcomeCounts };
}

/** All state, derived values, and the save handler for BulkResolveWithValueDialog. */
export function useBulkResolveWithValueForm(args: UseBulkResolveWithValueFormArgs) {
  const {
    open,
    rows,
    clearSelection,
    dismissOption,
    cantDetermineOption,
    otherMutationLoading,
    onClose,
    onConfirm,
    notifyMutated,
  } = args;

  const [answer, setAnswer] = useState<BulkAnswer | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) return;
    setAnswer(null);
    setNote("");
  }, [open]);

  const { plan, optionSummaries, copyWarnings, willUpdateById, outcomeCounts } = useBulkPreview({
    rows,
    answer,
    dismissOption,
    cantDetermineOption,
  });

  const { handleSave, resolving } = useBulkSaveHandler({
    rows,
    answer,
    plan,
    note,
    dismissOption,
    cantDetermineOption,
    onConfirm,
    clearSelection,
    onClose,
    notifyMutated,
  });

  const loading = resolving || otherMutationLoading;
  const saveDisabled = computeSaveDisabled({
    loading,
    answer,
    plan,
    dismissOption,
    cantDetermineOption,
  });

  return {
    title: formatBulkTitle(rows.length),
    answer,
    setAnswer,
    note,
    setNote,
    loading,
    resolving,
    copyTargets: answer && isBulkSource(answer) ? copyTargetStates(answer, rows) : [],
    optionSummaries,
    willUpdateById,
    sourceDisabledReasons: {
      EMANIFEST: sourceDisabledReason(rows, "EMANIFEST"),
      VENDOR: sourceDisabledReason(rows, "VENDOR"),
      DOCUMENT: sourceDisabledReason(rows, "DOCUMENT"),
    },
    copyWarnings,
    outcomeCounts,
    saveDisabled,
    handleSave,
  };
}
